import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRequireMfa } from '@/hooks/use-require-mfa';
import { supabase } from '@/integrations/supabase/client';
import { Helmet } from '@/components/Helmet';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

type RemovalRequestStatus =
  | 'pending'
  | 'confirmed'
  | 'rejected'
  | 'expired'
  | 'revoked'
  | 'superseded'
  | 'force_removed';

type RemovalStatusResponse = {
  success: boolean;
  request: {
    id: string;
    status: RemovalRequestStatus;
    reason: string | null;
    expiresAt: string | null;
    requestedAt: string;
    decidedAt: string | null;
    requestedBy: string;
    domain: {
      id: string;
      domainName: string;
      status: string;
      deletedAt: string | null;
    };
  };
};

const statusTitle = (status: RemovalRequestStatus) => {
  if (status === 'pending') return 'Confirm Domain Removal';
  if (status === 'confirmed') return 'Domain Removal Confirmed';
  if (status === 'rejected') return 'Domain Removal Rejected';
  if (status === 'expired') return 'Removal Request Expired';
  if (status === 'superseded') return 'Removal Request Replaced';
  if (status === 'force_removed') return 'Domain Already Removed';
  return 'Removal Request Unavailable';
};

const statusDescription = (status: RemovalRequestStatus) => {
  if (status === 'pending') {
    return 'Review this request. Confirming removes the domain without a cancellation fee.';
  }
  if (status === 'confirmed') return 'You already confirmed this removal request.';
  if (status === 'rejected') return 'You rejected this request and your domain remains active.';
  if (status === 'expired') return 'This request has expired. Ask support or your admin to send a new request.';
  if (status === 'superseded') return 'A newer request has replaced this one. Use the latest email link.';
  if (status === 'force_removed') return 'An admin force-removed this domain.';
  return 'This removal request is no longer active.';
};

export default function ConfirmDomainRemoval() {
  const { user, loading } = useAuth();
  const { checking } = useRequireMfa();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<RemovalStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<null | 'confirm' | 'reject'>(null);

  const requestToken = useMemo(() => searchParams.get('request')?.trim() || '', [searchParams]);

  const loadStatus = useCallback(async () => {
    if (!requestToken) {
      setStatus(null);
      setStatusError('The removal link is missing or invalid.');
      setStatusLoading(false);
      return;
    }

    setStatusLoading(true);
    setStatusError(null);
    try {
      const { data, error } = await supabase.functions.invoke('customer-domain-removal-request', {
        body: {
          action: 'status',
          requestToken,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to load removal request.');
      }
      setStatus(data as RemovalStatusResponse);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load removal request.';
      setStatusError(message);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [requestToken]);

  useEffect(() => {
    if (loading || checking) return;
    if (!user) {
      navigate('/portal/login', { replace: true, state: { from: '/portal/confirm-domain-removal' } });
      return;
    }
    void loadStatus();
  }, [checking, loadStatus, loading, navigate, user]);

  const handleDecision = async (action: 'confirm' | 'reject') => {
    if (!requestToken) return;
    setActionLoading(action);
    try {
      const { data, error } = await supabase.functions.invoke('customer-domain-removal-request', {
        body: {
          action,
          requestToken,
        },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || `Unable to ${action} request.`);
      }
      toast.success(action === 'confirm' ? 'Domain removal confirmed.' : 'Domain removal rejected.');
      await loadStatus();
    } catch (err) {
      const message = err instanceof Error ? err.message : `Unable to ${action} request.`;
      toast.error(message);
      await loadStatus();
    } finally {
      setActionLoading(null);
    }
  };

  if (loading || checking || statusLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Helmet>
          <title>Confirm Domain Removal | ShadowScan Portal</title>
          <meta
            name="description"
            content="Review and confirm or reject a pending ShadowScan domain removal request."
          />
        </Helmet>
        <div className="flex items-center gap-2 text-primary">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading removal request...</span>
        </div>
      </div>
    );
  }

  const currentStatus = status?.request?.status ?? 'revoked';
  const actionable = currentStatus === 'pending' && !statusError;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Helmet>
        <title>Confirm Domain Removal | ShadowScan Portal</title>
        <meta
          name="description"
          content="Confirm or reject a pending ShadowScan domain removal request from your portal."
        />
      </Helmet>
      <Card className="w-full max-w-2xl cyber-card">
        <CardHeader>
          <CardTitle>{statusTitle(currentStatus)}</CardTitle>
          <CardDescription>
            {statusError ? statusError : statusDescription(currentStatus)}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!!status?.request?.domain && (
            <div className="rounded-lg border border-border/50 bg-muted/20 p-4 space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Domain</span>
                <span className="font-mono text-foreground">{status.request.domain.domainName}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Requested by</span>
                <span className="text-foreground">{status.request.requestedBy}</span>
              </div>
              {status.request.reason && (
                <div className="flex items-start justify-between gap-2">
                  <span className="text-muted-foreground">Admin note</span>
                  <span className="text-foreground text-right">{status.request.reason}</span>
                </div>
              )}
              {status.request.expiresAt && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Expires</span>
                  <span className="text-foreground">{new Date(status.request.expiresAt).toLocaleString()}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Confirming removes this domain without a cancellation fee.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            {actionable ? (
              <>
                <Button
                  className="flex-1"
                  variant="destructive"
                  onClick={() => void handleDecision('confirm')}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === 'confirm' ? 'Confirming...' : 'Confirm removal'}
                </Button>
                <Button
                  className="flex-1"
                  variant="outline"
                  onClick={() => void handleDecision('reject')}
                  disabled={actionLoading !== null}
                >
                  {actionLoading === 'reject' ? 'Submitting...' : 'Keep domain'}
                </Button>
              </>
            ) : (
              <Button className="flex-1" onClick={() => navigate('/portal')}>
                Return to Portal
              </Button>
            )}
            <Button variant="outline" onClick={() => void loadStatus()} disabled={statusLoading || actionLoading !== null}>
              Refresh status
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
